import * as React from 'react'
import { theme } from '../../theme'
import { ImageCanvas } from '../../canvas/ImageCanvas'
import { MaskCanvas } from '../../canvas/MaskCanvas'
import { PromptInput } from '../../inpaint_and_erase/PromptInput'
import { DisclaimerTextCanvasPreview } from '../../disclaimer_texts/DisclaimerTextCanvasPreview'
import { LoadingOverlay } from './LoadingOverlay'

// Import the TDrawingTool type from MaskCanvas
type TDrawingTool = 'brush' | 'eraser' | null

// Define TextConfig type to match what's used in DisclaimerTextCanvasPreview
interface TextConfig {
	fontSize: number
	padding: number
	lineHeight: number
	textBoxHeight: number
	lines: string[]
	hasText?: boolean
	yOffset?: number
	linePositions?: number[]
}

interface Props {
	containerRef?: React.RefObject<HTMLDivElement>
	selectedAction: string
	imageCanvasRef: React.RefObject<HTMLCanvasElement>
	maskCanvasRef: React.RefObject<HTMLCanvasElement>
	previewRef: React.RefObject<HTMLCanvasElement>
	transform: {
		scale: number
		position: { x: number; y: number }
		isDragging: boolean
	}
	currentTool: TDrawingTool
	brushSize: number
	handleMouseDown: (e: React.MouseEvent) => void
	handleMouseMove: (e: React.MouseEvent) => void
	handleMouseUp: (e: React.MouseEvent) => void
	startDrawing: (e: React.MouseEvent) => void
	draw: (e: React.MouseEvent) => void
	stopDrawing: () => void
	showPromptInput: boolean
	showInputAbove: boolean
	prompt: string
	onPromptChange: (value: string) => void
	currentImage?: { url?: string }
	imageSrc?: string
	placeholderImage?: string
	text: string
	imageWidth: number
	imageHeight: number
	textConfig: TextConfig
	textAlignment: 'left' | 'center'
	isLoading: () => boolean
	getCurrentMessage: () => string | undefined
	getScaledDimensions: (width: number, height: number) => { width: number; height: number }
	dimensionDisplayWidth: number
	dimensionDisplayHeight: number
	onSetSelectedAction: (action: string) => void
	onImageUpload: (file: File) => void
}

export const MainImageCanvas = ({
	containerRef,
	selectedAction,
	imageCanvasRef,
	maskCanvasRef,
	previewRef,
	transform,
	currentTool,
	brushSize,
	handleMouseDown,
	handleMouseMove,
	handleMouseUp,
	startDrawing,
	draw,
	stopDrawing,
	showPromptInput,
	showInputAbove,
	prompt,
	onPromptChange,
	currentImage,
	imageSrc,
	text,
	imageWidth,
	imageHeight,
	textConfig,
	textAlignment,
	isLoading,
	getCurrentMessage,
	getScaledDimensions,
	dimensionDisplayWidth,
	dimensionDisplayHeight,
	onSetSelectedAction,
	onImageUpload,
}: Props) => {
	const scaledDimensions = getScaledDimensions(dimensionDisplayWidth, dimensionDisplayHeight)

	return (
		<div
			ref={selectedAction === 'adjust' ? containerRef : undefined}
			style={{
				position: 'relative',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				flex: 1,
				minHeight: 0,
				overflow: 'hidden',
			}}
		>
			<div
				style={{
					position: 'relative',
					width: `${scaledDimensions.width}px`,
					height: `${scaledDimensions.height}px`,
					border: `2px solid ${theme.colors.border}`,
					borderRadius: theme.borderRadius.sm,
					overflow: 'hidden',
					backgroundColor: theme.colors.surface,
				}}
				onMouseDown={selectedAction === 'adjust' ? handleMouseDown : undefined}
				onMouseMove={selectedAction === 'adjust' ? handleMouseMove : undefined}
				onMouseUp={selectedAction === 'adjust' ? handleMouseUp : undefined}
				onMouseLeave={selectedAction === 'adjust' ? handleMouseUp : undefined}
			>
				<div
					style={{
						position: 'relative',
						width: '100%',
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						transform:
							selectedAction === 'adjust'
								? `translate(${transform.position.x}px, ${transform.position.y}px) scale(${transform.scale})`
								: 'none',
						transformOrigin: 'center',
						transition: 'none',
						cursor: selectedAction === 'adjust' ? (transform.isDragging ? 'grabbing' : 'grab') : undefined,
					}}
				>
					<ImageCanvas ref={imageCanvasRef} />
					<MaskCanvas
						ref={maskCanvasRef}
						tool={currentTool}
						isActive={selectedAction === 'add' || selectedAction === 'erase'}
						brushSize={brushSize}
						onMouseDown={startDrawing}
						onMouseMove={draw}
						onMouseUp={stopDrawing}
						onMouseLeave={stopDrawing}
					/>
					<DisclaimerTextCanvasPreview
						ref={previewRef}
						text={text}
						isVisible={selectedAction === 'disclaimer'}
						width={imageWidth}
						height={imageHeight}
						textConfig={textConfig}
						textAlignment={textAlignment}
					/>

					{/* No image message overlay */}
					{!currentImage?.url && !imageSrc && (
						<div
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: 'rgba(0, 0, 0, 0.9)',
								color: 'white',
								padding: '20px',
								textAlign: 'center',
								zIndex: 10,
							}}
						>
							<div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
								No Images Available
							</div>
							<div style={{ fontSize: '14px', maxWidth: '80%', marginBottom: '15px' }}>
								Upload an image or use the Create with AI feature to start visualizing your campaign.
							</div>
							<div style={{ display: 'flex', gap: '10px' }}>
								{/* Upload Image button */}
								<button
									onClick={() => {
										// Create a temporary file input element
										const fileInput = document.createElement('input')
										fileInput.type = 'file'
										fileInput.accept = 'image/jpeg,image/jpg,image/png'
										fileInput.style.display = 'none'
										// Add event listener for when a file is selected
										fileInput.addEventListener('change', (e) => {
											const target = e.target as HTMLInputElement
											if (target.files && target.files.length > 0) {
												onImageUpload(target.files[0])
											}
											// Remove the element after use
											document.body.removeChild(fileInput)
										})
										// Add to DOM and trigger click
										document.body.appendChild(fileInput)
										fileInput.click()
									}}
									style={{
										padding: '10px 20px',
										borderRadius: theme.borderRadius.sm,
										border: 'none',
										backgroundColor: '#2F7B76',
										color: 'white',
										cursor: 'pointer',
										fontSize: '14px',
										fontWeight: 500,
										transition: 'background-color 0.2s ease',
									}}
									onMouseOver={(e) => {
										e.currentTarget.style.backgroundColor = 'rgba(47, 123, 118, 0.9)'
									}}
									onMouseOut={(e) => {
										e.currentTarget.style.backgroundColor = '#2F7B76'
									}}
								>
									Upload Image
								</button>
								{/* Create with AI button */}
								<button
									onClick={() => onSetSelectedAction('create')}
									style={{
										padding: '10px 20px',
										borderRadius: theme.borderRadius.sm,
										border: 'none',
										backgroundColor: '#2F7B76',
										color: 'white',
										cursor: 'pointer',
										fontSize: '14px',
										fontWeight: 500,
										transition: 'background-color 0.2s ease',
									}}
									onMouseOver={(e) => {
										e.currentTarget.style.backgroundColor = 'rgba(47, 123, 118, 0.9)'
									}}
									onMouseOut={(e) => {
										e.currentTarget.style.backgroundColor = '#2F7B76'
									}}
								>
									Create with AI
								</button>
							</div>
						</div>
					)}
				</div>
				<PromptInput
					show={showPromptInput}
					showAbove={showInputAbove}
					value={prompt}
					onChange={onPromptChange}
				/>
				{/* Using LoadingOverlay component */}
				<LoadingOverlay show={isLoading()} message={getCurrentMessage()} />
			</div>
		</div>
	)
}
